import {
  BarLayer,
  ChartFrame,
  Legend,
  stackExtent,
  StackedAreaLayer,
  sumBuckets,
  valueDomain,
  ZeroLine,
} from '@finsim/charts';
import { Landmark } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../shared/ui';
import { debtSeries, seriesColor } from '../lib/series';
import { ChartCard } from './chart-card';
import type { CanvasData } from './use-canvas';

const INTEREST = 'var(--chart-diverging-neg)';
const PRINCIPAL = 'var(--chart-diverging-pos)';

/** Loan balances over time, and yearly interest vs principal. */
export function Debt({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const { project, focused, focusedResult, valueMode, fmt, range, dates, buckets, selectedItemId } =
    data;

  const loans = useMemo(() => {
    if (!focusedResult || !focused) return [];
    const scenario = project.scenarios.find((s) => s.id === focused.id);
    const debts = debtSeries(project, scenario?.itemIds ?? [], focusedResult, valueMode, (name) =>
      t('charts.owedFor', { name }),
    );
    // Selecting a debt mutes the others; other items (an ETF, a salary) mute nothing.
    const selected = debts.some((d) => d.itemId === selectedItemId) ? selectedItemId : null;
    return debts.map((d, i) => ({
      ...d,
      color: seriesColor(i),
      muted: selected !== null && d.itemId !== selected,
    }));
  }, [project, focused, focusedResult, valueMode, selectedItemId, t]);

  const yearly = useMemo(() => {
    const interest = new Float64Array(buckets.length);
    const principal = new Float64Array(buckets.length);
    for (const l of loans) {
      sumBuckets(l.interest, buckets).forEach((v, i) => (interest[i] = (interest[i] ?? 0) + v));
      sumBuckets(l.principal, buckets).forEach((v, i) => (principal[i] = (principal[i] ?? 0) + v));
    }
    return { interest, principal };
  }, [loans, buckets]);

  if (!focused || !focusedResult) return null;
  if (loans.length === 0) {
    return (
      <EmptyState
        icon={<Landmark />}
        title={t('views.debt')}
        body={t('charts.noDebt', { name: focused.name })}
        className="flex-1 py-24"
      />
    );
  }

  const [, hi] = stackExtent(
    loans.map((l) => l.balance),
    dates.length,
  );
  const payoffs = data.events.filter((e) => e.tone === 'positive');
  const subtitle = [focused.name, data.moneyCaption].filter(Boolean).join(' · ');

  return (
    <div className="space-y-4 p-4">
      <ChartCard
        title={t('charts.debtTitle')}
        subtitle={subtitle}
        legend={
          <Legend
            label={t('charts.debtTitle')}
            items={loans.map((l) => ({ id: l.id, label: l.label, color: l.color }))}
          />
        }
        table={{
          caption: t('charts.debtTitle'),
          columns: [t('charts.year'), ...loans.map((l) => l.label)],
          rows: buckets.map((b) => ({
            label: String(b.year),
            values: loans.map((l) => fmt.money(l.balance[b.to] ?? 0)),
          })),
        }}
      >
        <ChartFrame
          markers={data.markers}
          dates={dates}
          range={range}
          height={300}
          domainSeries={[hi]}
          formatY={fmt.compact}
          formatXTick={fmt.year}
          ariaLabel={t('charts.debtAria', { name: focused.name })}
          events={payoffs}
          renderTooltip={(i) => (
            <div className="space-y-1">
              <p className="text-text-3">{dates[i] ? fmt.monthLong(dates[i]) : ''}</p>
              {loans.map((l) => (
                <p key={l.id} className="flex items-center justify-between gap-4">
                  <span className="flex items-center gap-1.5 text-text-2">
                    <span
                      aria-hidden
                      className="size-2 rounded-sm"
                      style={{ background: l.color }}
                    />
                    {l.label}
                  </span>
                  <span className="tabular">{fmt.money(l.balance[i] ?? 0)}</span>
                </p>
              ))}
            </div>
          )}
        >
          <StackedAreaLayer
            series={loans.map((l) => ({
              id: l.id,
              values: l.balance,
              color: l.color,
              muted: l.muted,
            }))}
          />
          <ZeroLine />
        </ChartFrame>
      </ChartCard>

      <ChartCard
        title={t('charts.interestVsPrincipal')}
        subtitle={subtitle}
        legend={
          <Legend
            label={t('charts.interestVsPrincipal')}
            items={[
              { id: 'interest', label: t('charts.interest'), color: INTEREST },
              { id: 'principal', label: t('charts.principal'), color: PRINCIPAL },
            ]}
          />
        }
        table={{
          caption: t('charts.interestVsPrincipal'),
          columns: [t('charts.year'), t('charts.interest'), t('charts.principal')],
          rows: buckets.map((b, i) => ({
            label: String(b.year),
            values: [fmt.money(yearly.interest[i] ?? 0), fmt.money(yearly.principal[i] ?? 0)],
          })),
        }}
      >
        <ChartFrame
          markers={data.markers}
          dates={dates}
          range={range}
          height={200}
          yDomain={valueDomain([
            Float64Array.from(yearly.interest, (v, i) => v + (yearly.principal[i] ?? 0)),
          ])}
          formatY={fmt.compact}
          formatXTick={fmt.year}
          ariaLabel={t('charts.interestVsPrincipalAria', { name: focused.name })}
          renderTooltip={(i) => {
            const b = buckets.findIndex((x) => i >= x.from && i <= x.to);
            const bucket = buckets[b];
            return bucket ? (
              <div className="space-y-1">
                <p className="text-text-3">{bucket.year}</p>
                <p className="flex justify-between gap-4">
                  <span className="text-text-2">{t('charts.interest')}</span>
                  <span className="tabular">{fmt.money(yearly.interest[b] ?? 0)}</span>
                </p>
                <p className="flex justify-between gap-4">
                  <span className="text-text-2">{t('charts.principal')}</span>
                  <span className="tabular">{fmt.money(yearly.principal[b] ?? 0)}</span>
                </p>
              </div>
            ) : null;
          }}
        >
          <ZeroLine />
          <BarLayer
            buckets={buckets}
            series={[
              { id: 'interest', values: yearly.interest, color: INTEREST },
              { id: 'principal', values: yearly.principal, color: PRINCIPAL },
            ]}
          />
        </ChartFrame>
      </ChartCard>
    </div>
  );
}
